import type { CSSProperties } from "react";

interface RadioMarkProps {
  checked: boolean;
  /**
   * `md` (default) is the 22px mark on a selectable row — a payment invoice, an
   * organization to switch to. `sm` is the 13px one that sits inside a pill or
   * beside a short label.
   */
  size?: "sm" | "md" | undefined;
}

/**
 * The ring-and-fill radio affordance, with no state of its own — the row or pill
 * around it owns that.
 *
 * Distinct from `RadioGroup`, which renders real inputs with their own label
 * rows and is the right answer for an actual form field. This is for a whole row
 * that is itself the target: you click the invoice, not a control inside it. And
 * distinct from `StatusDot`, which is a solid pip rather than a ring.
 */
export function RadioMark({ checked, size = "md" }: RadioMarkProps) {
  const small = size === "sm";

  // The large dot is sized as a percentage so it scales with its ring; no
  // Tailwind size class can say "78% of my parent".
  const innerDot: CSSProperties = small
    ? { width: 9, height: 9 }
    : { width: "78%", height: "78%" };

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full ${
        small ? "h-[13px] w-[13px] border" : "h-[22px] w-[22px] border-2"
      } ${
        // The small mark keeps a brand ring whether or not it's checked: it
        // appears beside a label that already reads as an action ("Select All"),
        // where a grey ring looks disabled.
        checked || small ? "border-brand" : "border-mid-gray"
      }`}
    >
      {checked && <div className="rounded-full bg-blue" style={innerDot} />}
    </div>
  );
}
